import { useMemo } from 'react';
import {
  ExpressionSettingsLineType,
  IExpressionSettings,
} from '../../../interfaces';
import { useAppDispatch } from '../../../../../redux/hooks';
import { setChartSettingsExpressionSettingsAction } from '../../../../../redux/slices/analyses';
import ThinDivider from '../../../../ThinDivider';
import KubitTooltip from '../../../../KubitTooltip';
import CheckboxDropdownOption from '../../../../Dropdown/CheckboxDropdownOption';

interface IProps {
  analysisId: string;
  expressionSettings: IExpressionSettings;
  dataPointsAmount: number;
  isMeasureToggleDisabled: boolean;
  showCompareOption: boolean;
}

const rollingLabelByType: Record<ExpressionSettingsLineType, string> = {
  [ExpressionSettingsLineType.Ma]: 'Rolling Average 7',
  [ExpressionSettingsLineType.Ma30]: 'Rolling Average 30',
  [ExpressionSettingsLineType.Ms7]: 'Rolling Sum 7',
  [ExpressionSettingsLineType.Ms30]: 'Rolling Sum 30',
  [ExpressionSettingsLineType.CumulativeSum]: 'Cumulative Sum',
};

export default ({
  analysisId,
  expressionSettings,
  dataPointsAmount,
  isMeasureToggleDisabled,
  showCompareOption,
}: IProps) => {
  const dispatch = useAppDispatch();

  const toggleMeasure = () => {
    if (isMeasureToggleDisabled) {
      return;
    }
    dispatch(
      setChartSettingsExpressionSettingsAction({
        analysisId,
        expressionSettings: {
          ...expressionSettings,
          showMeasure: !expressionSettings.showMeasure,
        },
      }),
    );
  };

  const lineTypeList = useMemo(() => {
    if (!dataPointsAmount) {
      return [];
    }

    const types: ExpressionSettingsLineType[] = [
      ExpressionSettingsLineType.CumulativeSum,
    ];

    // Depending on available amount of data points, we will
    // show different options
    if (dataPointsAmount >= 7 && dataPointsAmount < 30) {
      types.unshift(
        ExpressionSettingsLineType.Ma,
        ExpressionSettingsLineType.Ms7,
      );
    } else if (dataPointsAmount >= 30) {
      types.unshift(
        ExpressionSettingsLineType.Ma,
        ExpressionSettingsLineType.Ma30,
        ExpressionSettingsLineType.Ms7,
        ExpressionSettingsLineType.Ms30,
      );
    }

    return types.map((type) => ({
      label: rollingLabelByType[type],
      value: type,
    }));
  }, [dataPointsAmount]);

  const handleChangeLineType = (lineType: ExpressionSettingsLineType) => {
    dispatch(
      setChartSettingsExpressionSettingsAction({
        analysisId,
        expressionSettings: {
          ...expressionSettings,
          lineType: expressionSettings.lineType === lineType ? '' : lineType,
        },
      }),
    );
  };

  const toggleCompareMeasures = () => {
    dispatch(
      setChartSettingsExpressionSettingsAction({
        analysisId,
        expressionSettings: {
          ...expressionSettings,
          showCompare: !expressionSettings.showCompare,
        },
      }),
    );
  };

  return (
    <>
      <KubitTooltip
        disabled={!isMeasureToggleDisabled}
        content={'Not available for Metric Chart.'}
        placement="top"
      >
        <CheckboxDropdownOption
          onClick={toggleMeasure}
          label={'Measure'}
          isChecked={expressionSettings.showMeasure}
          isDisabled={isMeasureToggleDisabled}
        />
      </KubitTooltip>
      {showCompareOption && (
        <KubitTooltip
          disabled={!isMeasureToggleDisabled}
          content={'Not available for Metric Chart.'}
          placement="top"
        >
          <CheckboxDropdownOption
            onClick={toggleCompareMeasures}
            label={'Compare Measures'}
            isChecked={expressionSettings.showCompare}
            isDisabled={isMeasureToggleDisabled}
          />
        </KubitTooltip>
      )}

      {lineTypeList.length > 0 && (
        <>
          <ThinDivider layout="horizontal" size="full" />
          {lineTypeList.map((item) => (
            <CheckboxDropdownOption
              key={item.value}
              onClick={() => handleChangeLineType(item.value)}
              label={item.label}
              isChecked={expressionSettings.lineType === item.value}
            />
          ))}
        </>
      )}
    </>
  );
};
